import PropTypes from 'prop-types';
import React from 'react';
import { FullscreenSidebar } from '@99designs/common/components/Fullscreen';
import DesignDetailsLoggedOutPane from './design-details-logged-out-pane';
import MessageMarker from 'contest/components/message-marker';
import RatingAndShareDesignAction from './rating-and-share-design-action';
import StatusDesignAction from './status-design-action';
import MainDesignAction from './main-design-action';
import AdditionalDesignActions from './additional-design-actions';
import CommentPane from './comment-pane';
import DesignDetailsTooltip from './design-details-tooltip';

import { DisplayName } from '@99designs/common/components/DisplayName';
import SidebarHeader from '@99designs/common/apps/ddo/components/SidebarHeader';
import { Pill } from '@99designs/common/components/Pill';
import { Icon } from '@99designs/common/components/Icon';
import { RelativeTime } from '@99designs/common/components/RelativeTime';
import { LoadableElement } from '@99designs/common/components/LoadableElement';

import ConnectedStatusIndicator from 'contest/components/connected/connected-status-indicator';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';

import propertyOrNull from 'contest/utils/property-or-null';

const SIDEBAR_MODE_DEFAULT = 'default';
const SIDEBAR_MODE_STOCK_LIST = 'stock';
const SIDEBAR_MODE_GENERATIVE_AI = 'ai';

export default class DesignDetailsSidebar extends React.Component {
    constructor() {
        super();
        this.state = {
            activePane: SIDEBAR_MODE_DEFAULT,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.design.designId !== this.props.design.designId) {
            this.setState(() => ({ activePane: SIDEBAR_MODE_DEFAULT }));
        }
    }

    render() {
        if (
            this.state.activePane === SIDEBAR_MODE_STOCK_LIST ||
            this.state.activePane === SIDEBAR_MODE_GENERATIVE_AI
        ) {
            return this.sidebar();
        }

        return this.defaultSidebar();
    }

    stockListSidebarBody() {
        const { stockDeclarations } = this.props.design;

        return (
            <FullscreenSidebar.Body>
                {stockDeclarations.map((item) => (
                    <a
                        rel="nofollow noopener noreferrer"
                        href={item.url}
                        target="_blank"
                        key={item.stock_declaration_id}
                        className="section-action section-action--joined"
                    >
                        <span className="section-action--joined__inner">
                            <span className="section-action__icon--left">
                                <Icon type={'document-1'} />
                            </span>
                            <span className="section-action__label">
                                {item.name}
                                <span className="section-action__label__meta">
                                    {this.getStockPriceMessage(item)}
                                </span>
                            </span>
                        </span>
                    </a>
                ))}
            </FullscreenSidebar.Body>
        );
    }

    generativeAiSidebarBody() {
        const { generativeAiDeclaration } = this.props.design;
        const { generative_ai_declaration_id, tool, description } = generativeAiDeclaration[0];

        return (
            <FullscreenSidebar.Body>
                <span key={generative_ai_declaration_id} className="section-action--joined__inner">
                    <span className="section-action__label">
                        <p>
                            <strong>
                                <FormattedMessage id="generative_ai_tools_used_description" />
                            </strong>
                        </p>
                    </span>
                    <span className="section-action__label">
                        <p>
                            <span className="section-action__description block">
                                <strong>
                                    <FormattedMessage id="generative_ai_tools_used" />
                                </strong>
                                {DesignDetailsTooltip('generative_ai_tools_used_tooltip')}
                                <br />
                                {tool}
                            </span>
                        </p>
                        <p>
                            <span className="section-action__description block">
                                <strong>
                                    <FormattedMessage id="generative_ai_tools_used_designer_description" />
                                </strong>
                                {DesignDetailsTooltip(
                                    'generative_ai_tools_used_designer_description_tooltip'
                                )}
                                <br />
                                {description}
                            </span>
                        </p>
                    </span>
                </span>
            </FullscreenSidebar.Body>
        );
    }

    defaultSidebar() {
        return (
            <FullscreenSidebar.Main>
                <FullscreenSidebar.Header>
                    <SidebarHeader.Main
                        closeOverlayHandler={this.props.closeOverlayHandler}
                        isCloseButtonEnabled={this.props.isInline}
                    >
                        <SidebarHeader.Section>
                            <SidebarHeader.Heading>
                                <SidebarHeader.HeadingText>
                                    {'#' + this.props.design.designId}
                                </SidebarHeader.HeadingText>
                                {this.contestStatusPill()}
                            </SidebarHeader.Heading>
                            <SidebarHeader.SubHeading>
                                <FormattedMessage
                                    id="designed_by_user_and_time"
                                    values={{
                                        user: <DisplayName user={this.props.design.creator} />,
                                        timeAgo: (
                                            <RelativeTime date={this.props.design.createdAt} />
                                        ),
                                    }}
                                />
                                {this.onlineStatus()}
                                {this.newPill()}
                            </SidebarHeader.SubHeading>
                            {this.backToContestMessage()}
                        </SidebarHeader.Section>

                        {this.stockDeclarationsSection()}
                        {this.generativeAiDeclarationSection()}

                        <SidebarHeader.Section>
                            <div className="design-actions-panel">
                                <RatingAndShareDesignAction
                                    design={this.props.design}
                                    activeUser={this.props.activeUser}
                                    shareURLs={{
                                        facebook: this.props.design.links.facebook,
                                        twitter: this.props.design.links.twitter,
                                        pinterest: this.props.design.links.pinterest,
                                        shortURL: this.props.design.links.shortURL,
                                    }}
                                />
                                <StatusDesignAction
                                    design={this.props.design}
                                    activeUser={this.props.activeUser}
                                />
                                <MainDesignAction
                                    design={this.props.design}
                                    activeUser={this.props.activeUser}
                                />
                                <AdditionalDesignActions
                                    design={this.props.design}
                                    activeUser={this.props.activeUser}
                                />
                            </div>
                        </SidebarHeader.Section>
                    </SidebarHeader.Main>
                </FullscreenSidebar.Header>
                {this.defaultSidebarBody()}
            </FullscreenSidebar.Main>
        );
    }

    defaultSidebarBody() {
        if (!this.props.activeUser) {
            return (
                <FullscreenSidebar.Body>
                    <DesignDetailsLoggedOutPane urls={this.props.urls} />
                </FullscreenSidebar.Body>
            );
        } else if (this.props.design.permission.canViewComments) {
            return (
                <FullscreenSidebar.Body>
                    <LoadableElement
                        variations={['align-top', 'padded']}
                        spinnerVariations={['inline']}
                        isLoading={this.props.isLoadingComments || !this.props.isLoaded}
                    >
                        <CommentPane
                            comments={this.props.comments}
                            design={this.props.design}
                            activeUser={this.props.activeUser}
                            recipientUser={this.props.recipientUser}
                            canShowCommentEditing={
                                this.props.selectedCommentEditingLocation === 'pane'
                            }
                            hasCommentForm={this.props.design.permission.canComment}
                            selectedComment={this.props.selectedComment}
                            selectedCommentStatus={this.props.selectedCommentStatus}
                            selectedCommentEditingLocation={
                                this.props.selectedCommentEditingLocation
                            }
                        />
                    </LoadableElement>
                    {this.handoverMessage()}
                </FullscreenSidebar.Body>
            );
        }
    }

    sidebar() {
        return (
            <FullscreenSidebar.Main>
                <FullscreenSidebar.Header>
                    <SidebarHeader.Main
                        closeOverlayHandler={this.props.closeOverlayHandler}
                        isCloseButtonEnabled={this.props.isInline}
                    >
                        <SidebarHeader.Section>
                            <SidebarHeader.Heading>
                                <SidebarHeader.HeadingText>
                                    {'#' + this.props.design.designId}
                                </SidebarHeader.HeadingText>
                                {this.contestStatusPill()}
                            </SidebarHeader.Heading>
                            <SidebarHeader.SubHeading>
                                <FormattedMessage
                                    id="designed_by_user_and_time"
                                    values={{
                                        user: <DisplayName user={this.props.design.creator} />,
                                        timeAgo: (
                                            <RelativeTime date={this.props.design.createdAt} />
                                        ),
                                    }}
                                />
                                {this.onlineStatus()}
                                {this.newPill()}
                            </SidebarHeader.SubHeading>
                            {this.backToContestMessage()}
                        </SidebarHeader.Section>

                        <button
                            className="section-action section-action--primary"
                            onClick={() =>
                                this.setState(() => ({ activePane: SIDEBAR_MODE_DEFAULT }))
                            }
                        >
                            <span className="section-action__icon--left">
                                <Icon type={'left-2'} />
                            </span>

                            <span className="section-action__label">
                                <FormattedMessage id="back_to_overview" />
                            </span>
                        </button>
                    </SidebarHeader.Main>
                </FullscreenSidebar.Header>
                {this.state.activePane === SIDEBAR_MODE_STOCK_LIST && this.stockListSidebarBody()}
                {this.state.activePane === SIDEBAR_MODE_GENERATIVE_AI &&
                    this.generativeAiSidebarBody()}
            </FullscreenSidebar.Main>
        );
    }

    stockDeclarationsSection() {
        if (
            !propertyOrNull(this.props.design.stockDeclarations, 'length') ||
            this.props.design.stockDeclarations.length === 0
        ) {
            return null;
        }

        const hasPaidStock =
            this.props.design.stockDeclarations.filter((x) => x.requires_purchase).length > 0;

        return (
            <button
                className="section-action section-action--primary"
                onClick={() => this.setState(() => ({ activePane: SIDEBAR_MODE_STOCK_LIST }))}
            >
                <span className="section-action__icon--left">
                    <Icon type={'document-1'} />
                </span>
                <span className="section-action__label">
                    <FormattedMessage id={hasPaidStock ? 'paid_stock_images' : 'stock_images'} />
                </span>
                <span className="section-action__icon--right">
                    <Icon type={'right-2'} />
                </span>
                <span className="section-action__count">
                    {this.props.design.stockDeclarations.length}
                </span>
            </button>
        );
    }

    generativeAiDeclarationSection() {
        const { generativeAiDeclaration } = this.props.design;

        if (!generativeAiDeclaration || !generativeAiDeclaration[0]) {
            return null;
        }

        return (
            <button
                className="section-action section-action--primary"
                onClick={() => this.setState(() => ({ activePane: SIDEBAR_MODE_GENERATIVE_AI }))}
            >
                <span className="section-action__icon--left">
                    <Icon type={'document-1'} />
                </span>
                <span className="section-action__label">
                    <FormattedMessage id={'generative_ai_used'} />
                </span>
                <span className="section-action__icon--right">
                    <Icon type={'right-2'} />
                </span>
                <span className="section-action__count">
                    {this.props.design.generativeAiDeclaration.length}
                </span>
            </button>
        );
    }

    backToContestMessage() {
        if (!this.props.isInline) {
            return (
                <SidebarHeader.SubHeading>
                    <FormattedHTMLMessage id="back_to_contest" />
                </SidebarHeader.SubHeading>
            );
        }
    }

    contestStatusPill() {
        if (this.props.design.status !== 'visible') {
            return (
                <SidebarHeader.HeadingPill>
                    <Pill>
                        <FormattedMessage id={`${this.props.design.status}_status`} />
                    </Pill>
                </SidebarHeader.HeadingPill>
            );
        } else {
            return null;
        }
    }

    newPill() {
        if (!this.props.design.isSeen && this.props.design.permission.canViewNewBadge) {
            return (
                <Pill variations={['small', 'callout', 'offset-left']}>
                    <FormattedMessage id="entry_new" />
                </Pill>
            );
        } else {
            return null;
        }
    }

    onlineStatus() {
        if (this.props.activeUser) {
            return (
                <ConnectedStatusIndicator
                    user={this.props.design.creator}
                    lastSeenTimesUrl={this.props.design.links.lastSeenTime}
                />
            );
        }
    }

    handoverMessage() {
        if (this.props.design.links.handoverMessages) {
            const handoverAnchor = (
                <a href={this.props.design.links.handoverMessages} className="link">
                    <FormattedMessage id="your_conversation" />
                </a>
            );
            return (
                <div className="design-details-handover-message">
                    <MessageMarker variations={['center']}>
                        <FormattedMessage id="end_of_contest_marker" />
                    </MessageMarker>
                    <FormattedMessage
                        id="continue_conversation"
                        values={{
                            yourConversation: handoverAnchor,
                            entryId: this.props.design.entryId,
                        }}
                    />
                </div>
            );
        }
    }

    getStockPriceMessage(item) {
        if (!item.requires_purchase) {
            return <FormattedMessage id="stock-declarations-free" />;
        } else if (item.requires_purchase && !item.purchasable) {
            return <FormattedMessage id="stock-declarations-buy" />;
        } else {
            return this.props.activeUser && this.props.activeUser.isDesigner ? (
                <FormattedMessage id="stock-declarations-buy-partner_designer" />
            ) : (
                <FormattedMessage id="stock-declarations-buy-partner_ch" />
            );
        }
    }
}

DesignDetailsSidebar.contextTypes = {
    getStore: PropTypes.func,
};

DesignDetailsSidebar.propTypes = {
    design: PropTypes.object.isRequired,
    urls: PropTypes.object.isRequired,
    activeUser: PropTypes.object,
    recipientUser: PropTypes.object,
    closeOverlayHandler: PropTypes.func,
    isInline: PropTypes.bool.isRequired,
    isLoadingComments: PropTypes.bool.isRequired,
    isLoaded: PropTypes.bool,
    comments: PropTypes.array.isRequired,
    selectedComment: PropTypes.object,
    selectedCommentStatus: PropTypes.oneOf(['creating', 'editing', 'saving', 'deleting', 'error']),
    selectedCommentEditingLocation: PropTypes.oneOf(['canvas', 'pane']),
};

DesignDetailsSidebar.defaultProps = {
    closeOverlayHandler() {},
    isInline: true,
    isLoadingComments: false,
    isLoaded: true,
};



// WEBPACK FOOTER //
// ./src/web/js/contest/apps/design-details/components/design-details-sidebar.jsx